import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

export interface SpotlightStep {
  /** value of the data-tour attribute on the target element */
  target: string;
  title: string;
  body: string;
}

interface SpotlightTourProps {
  steps: SpotlightStep[];
  open: boolean;
  onClose: (completed: boolean) => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;

const findTarget = (target: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-tour="${target}"]`);

export const SpotlightTour: React.FC<SpotlightTourProps> = ({ steps, open, onClose }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  // Only keep steps whose target is actually rendered.
  const visibleSteps = useMemo(() => {
    if (!open) return [];
    return steps.filter((s) => !!findTarget(s.target));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, open]);

  const step = visibleSteps[index];

  const measure = useCallback(() => {
    if (!step) return;
    const el = findTarget(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    });
  }, [step]);

  // Scroll target into view then measure.
  useEffect(() => {
    if (!open || !step) return;
    const el = findTarget(step.target);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(measure, 350);
    return () => window.clearTimeout(timer);
  }, [open, step, measure]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const onChange = () => measure();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    const el = step ? findTarget(step.target) : null;
    let observer: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(onChange);
      observer.observe(el);
    }
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
      observer?.disconnect();
    };
  }, [open, step, measure]);

  const finish = useCallback((completed: boolean) => {
    setIndex(0);
    onClose(completed);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, finish]);

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  if (!open || !step || typeof document === 'undefined') return null;

  const isLast = index === visibleSteps.length - 1;

  // Card placement: mobile pins to the bottom, desktop sits under/above the spotlight.
  const cardStyle: React.CSSProperties = (() => {
    if (isMobile || !rect) {
      return { left: 16, right: 16, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' };
    }
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const width = 340;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - width / 2, 16),
      window.innerWidth - width - 16
    );
    return spaceBelow > 220
      ? { top: rect.top + rect.height + 12, left, width }
      : { bottom: window.innerHeight - rect.top + 12, left, width };
  })();

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="spotlight-tour"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100]"
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
      >
        {/* Dim overlay with a cut-out around the target */}
        <div className="absolute inset-0" onClick={() => finish(false)}>
          {rect ? (
            <motion.div
              layout
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className="absolute rounded-2xl ring-2 ring-primary pointer-events-none"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px hsl(var(--background) / 0.85)',
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-background/85" />
          )}
        </div>

        {/* Tooltip card */}
        <motion.div
          key={step.target}
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 26 }}
          className="fixed rounded-2xl border border-border bg-card p-4 shadow-xl"
          style={cardStyle}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs font-medium text-primary">
              {t('tour.stepCounter', { current: index + 1, total: visibleSteps.length })}
            </div>
            <button
              type="button"
              aria-label={t('tour.skip')}
              onClick={() => finish(false)}
              className="-mr-1 -mt-1 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <h3 className="mt-1 text-base font-semibold text-foreground">{step.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>

          <div className="mt-4 flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="min-h-11 text-muted-foreground"
              onClick={() => finish(false)}
            >
              {t('tour.skip')}
            </Button>
            <div className="flex items-center gap-2">
              {index > 0 && (
                <Button
                  variant="outline"
                  className="min-h-11"
                  onClick={() => setIndex((i) => Math.max(0, i - 1))}
                >
                  {t('tour.back')}
                </Button>
              )}
              <Button
                className="min-h-11"
                onClick={() => (isLast ? finish(true) : setIndex((i) => i + 1))}
              >
                {isLast ? t('tour.done') : t('tour.next')}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default SpotlightTour;
